import React, { useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";
import { QRCodeCanvas } from "qrcode.react";
import { useLocation, useNavigate } from "react-router-dom";

// Professional Image Component for ID Cards
const ProfessionalImageCard = ({ src, alt, className = "", containerClassName = "" }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  return (
    <div className={`relative ${containerClassName}`}>
      {/* Loading placeholder */}
      {!imageLoaded && !imageError && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
          <div className="text-gray-400 text-sm">Loading...</div>
        </div>
      )}

      {/* Error placeholder */}
      {imageError && (
        <div className="absolute inset-0 bg-gray-200 flex items-center justify-center">
          <div className="text-gray-400 text-sm">Image not available</div>
        </div>
      )}

      {/* Actual image */}
      <img
        src={imageError ? "/images/placeholder.png" : src}
        alt={alt}
        className={`${className} transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
        style={{
          objectFit: 'cover',
          objectPosition: 'center',
          maxWidth: '100%',
          maxHeight: '100%',
          width: '100%',
          height: '100%'
        }}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
    </div>
  );
};

const DownloadReporterIdCards = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { reporter } = location.state || {};

  const [idCardData, setIdCardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showIdCard, setShowIdCard] = useState(false);
  const [message, setMessage] = useState("");


  // Function to download ID cards as PDF
  const downloadIdCards = async () => {
    const html2canvas = await import("html2canvas");
    const jsPDF = (await import("jspdf")).jsPDF;

    const front = document.getElementById("card-front");
    const back = document.getElementById("card-back");

    if (!front || !back) {
      toast.error("Card front or back not found");
      return;
    }

    const pdf = new jsPDF("portrait", "mm", [82, 127]);

    // Wait for fonts
    await document.fonts.ready;

    // Render front
    const frontCanvas = await html2canvas.default(front, {
      scale: 2,
      useCORS: true,
    });
    const frontImg = frontCanvas.toDataURL("image/png");
    pdf.addImage(frontImg, "PNG", 0, 0, 82, 127);

    // Render back
    const backCanvas = await html2canvas.default(back, {
      scale: 2,
      useCORS: true,
    });
    const backImg = backCanvas.toDataURL("image/png");
    pdf.addPage();
    pdf.addImage(backImg, "PNG", 0, 0, 82, 127);

    pdf.save("IINSAF_ID_Card.pdf");
  };

  return (
    <div className="reporter-id-card-container">
      {/* Display message if it exists */}
      {message && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="message-container max-w-md mx-4 p-6 bg-white border border-blue-300 rounded-lg shadow-lg">
            <p className="text-blue-800 text-center text-lg">{message}</p>
            <button
              onClick={() => setMessage("")}
              className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Check if reporter data exists */}
      {!reporter ? (
        <div className="text-center py-8">
          <p className="text-red-600 text-lg">No reporter data available</p>
        </div>
      ) : (
        <div className="space-y-12">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-red-600 to-blue-800">
                Your IINSAF Cards
              </span>
            </h2>

            {/* Download Button */}
            <button
              onClick={downloadIdCards}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 rounded-lg shadow-md transition duration-300 mb-8"
            >
              📥 Download ID Cards
            </button>
          </div>

          {/* ID Cards Container */}
          <div id="id-cards-container" className="bg-white p-4">
            {/* Map through reporterIdCards */}
            {reporter && [reporter].filter(card => card).map((card, index) => (
              <div
                key={card?._id || index}
                className="flex flex-col lg:flex-row justify-center gap-8 px-4 perspective-1000 mb-12"
              >
                {/* IINSAF Press Card - Enhanced */}
                <div
                  id="card-front"
                  className=" w-[400px] h-[770px]  bg-white flex"
                >
                  {/* Vertical Red Section */}
                  <div className="bg-red-600 w-[49px] flex items-center justify-center text-white text-[24px] text-center font-bold relative pl-2">
                    <span className="rotate-90 whitespace-nowrap text-[32px] font-bold tracking-[0.6em] leading-none ml-4">
                      RAISE YOUR VOICE
                    </span>
                  </div>

                  {/* Main Card Content */}
                  <div className="bg-white/80 backdrop-blur-sm rounded-r-xl shadow-2xl overflow-hidden w-full lg:w-[400px] border-t border-white/50 relative">
                    <div className="p-2 relative">
                      {/* Background pattern */}
                      <div className="absolute inset-0 opacity-5">
                        <div className="absolute -right-20 -top-20 w-40 h-40 rounded-full bg-red-600"></div>
                        <div className="absolute -left-20 -bottom-20 w-40 h-40 rounded-full bg-blue-600"></div>
                      </div>

                      <div className="flex flex-col items-center mb-4 relative">
                        <img
                          src="/images/iinsaf.png"
                          alt="IINSAF Logo"
                          className="h-16 mt-4"
                        />
                        <div className="text-center">
                          <p className="text-sm font-semibold text-gray-800">
                            <span className="text-red-600 font-bold">
                              International Influencing
                            </span>{" "}
                            News Social Media Advertisement Federation
                          </p>
                        </div>
                        <div className="flex justify-center">
                          <div className="flex justify-center">
                            <img
                              src="/assets/presslog.png"
                              alt="Press Logo"
                              className="h-18 w-24 "
                            />
                          </div>
                        </div>

                        <p className="flex justify-between">
                          <span className="text-black font-medium">
                            IINSAF ID:
                          </span>
                          <span className="font-mono ml-1">
                            {card?.iinsafId}
                          </span>
                        </p>
                      </div>

                      {/* Photo Section - Enhanced */}
                      <div className="flex flex-col items-center">
                        <div className="w-40 h-40 bg-gray-200 rounded-lg overflow-hidden mb-1 shadow-lg border-2 border-red-200 relative">
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent z-10"></div>
                          <ProfessionalImageCard
                            src={card?.image || "/images/placeholder.png"}
                            alt="Reporter"
                            className="w-full h-full"
                            containerClassName="w-full h-full"
                          />
                        </div>
                      </div>

                      {/* Details Section - Enhanced */}
                      <div className="text-black">
                        <div className="space-y-3 bg-white/50 p-4 rounded-lg">
                          <p className="flex justify-between">
                            <span className="text-black font-medium">
                              Name:
                            </span>
                            <span className="font-mono">{card?.name}</span>
                          </p>
                          <p className="flex justify-between">
                            <span className="text-black font-medium">
                              Phone:
                            </span>
                            <span className="font-mono">
                              {card?.mobileNo}
                            </span>
                          </p>
                          <p className="flex justify-between">
                            <span className="text-black font-medium">DOB:</span>
                            <span className="font-mono">
                              {card?.dateOfBirth}
                            </span>
                          </p>
                          <p className="flex justify-between">
                            <span className="text-black font-medium">
                              Blood Type:
                            </span>
                            <span className="font-mono font-bold text-red-600">
                              {card?.bloodGroup}
                            </span>
                          </p>
                          <p className="flex justify-between">
                            <span className="text-black font-medium">
                              Issue Date:
                            </span>
                            <span className="font-mono font-bold text-blue-600">
                              {card?.issuedDate}
                            </span>
                          </p>
                          <p className="flex justify-between">
                            <span className="text-black font-medium">
                              Valid Up To:
                            </span>
                            <span className="font-mono font-bold text-green-600">
                              {card?.validUpto}
                            </span>
                          </p>

                          <div className="relative flex flex-col items-end mt-4 h-14">
                            <span className="text-black font-medium">
                              Approved By:
                            </span>

                            {/* IINSAF Authority text (underneath the signature) */}
                            <span className=" text-sm mt-1 z-0">
                              IINSAF Authority
                            </span>

                            {/* Signature image overlaid on the name */}
                            <img
                              src="/assets/signature.png"
                              alt="Signature"
                              className="absolute top-[10%] right-0 h-20 transform rotate-[-20deg] z-10 opacity-90 pointer-events-none"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* IINSAF ID Card - Enhanced */}
                <div
                  id="card-back"
                  className="w-[400px] h-[770px] bg-white flex"
                >
                  {/* Vertical Red Section */}
                  <div className="bg-red-600 w-[49px] flex items-center justify-center text-white text-[24px] text-center font-bold relative pl-2">
                    <span className="rotate-90 whitespace-nowrap text-[38px] font-extrabold tracking-[1.5em] leading-none ml-4">
                      ii<span className="text-black">nsaf</span>
                    </span>
                  </div>



                  <div className="bg-white/80 backdrop-blur-sm rounded-r-xl shadow-2xl overflow-hidden w-full lg:w-[400px] border-t border-white/50 relative">
                    <div className="pl-4 relative">
                      <img
                        src="/CompanyImages/insaaf_logo-1.png"
                        alt="Logo"
                        className="h-32 w-28 object-contain"
                      />
                      {/* Background pattern */}
                      <div className="absolute inset-0 opacity-5">
                        <div className="absolute -right-20 -top-20 w-40 h-40 rounded-full bg-red-600"></div>
                        <div className="absolute -left-20 -bottom-20 w-40 h-40 rounded-full bg-blue-600"></div>
                      </div>

                      <div className="flex flex-col items-center ">
                        <div className="flex justify-center">
                          <img
                            src="/assets/presslog.png"
                            alt="Press Logo"
                            className="h-18 w-24 -mt-24 mb-8"
                          />
                        </div>

                      </div>
                      <p className="flex gap-2 mb-10 -mt-6">
                        <span className="text-black font-medium">
                          Channel Name:
                        </span>
                        <span className="font-bold">
                          {card?.channelName}
                        </span>
                      </p>

                      {/* Channel Logo Section - Enhanced */}
                      <div className="flex flex-col items-center mb-6">
                        <div className="w-40 h-40 bg-gray-200 rounded-lg overflow-hidden mb-4 shadow-lg border-2 border-red-200 relative">
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent z-10"></div>
                          <ProfessionalImageCard
                            src={card?.channelLogo || "/images/placeholder.png"}
                            alt="channel logo"
                            className="w-full h-full"
                            containerClassName="w-full h-full"
                          />
                        </div>
                      </div>

                      {/* Details Section - Enhanced */}
                      <div className="text-black">
                        <div className="space-y-3 bg-white/50 p-4 rounded-lg">
                          <p className="flex justify-between">
                            <span className="text-black font-medium">
                              Aadhar No:
                            </span>
                            <span className="font-mono">
                              {card?.aadharNo}
                            </span>
                          </p>

                          <p className="flex justify-between">
                            <span className="text-black font-medium">
                              Address:
                            </span>
                            <span className="text-right flex-1 ml-4 text-sm">
                              {card?.ResidentialAddress}
                            </span>
                          </p>

                          <p className="flex justify-between">
                            <span className="text-black font-medium">
                              State:
                            </span>
                            <span className="text-right flex-1 ml-4 text-sm">
                              {card?.state}
                            </span>
                          </p>
                          <p className="flex justify-between">
                            <span className="text-black font-medium">
                              City:
                            </span>
                            <span className="text-right flex-1 ml-4 text-sm">
                              {card?.city}
                            </span>
                          </p>
                        </div>
                      </div>

                      {/* QR Code and Website - Enhanced */}
                      <div className="mt-2 pt-4 pb-10 border-t border-red-400 text-center space-y-2">
                        <div className="flex justify-center">
                          <div className="h-24 w-24 p-1 mt-2 bg-white rounded-lg shadow-md hover:shadow-lg transition-all">
                            <QRCodeCanvas
                              value={`${process.env.REACT_APP_API_BASE_URL}/reporter/view/${card?._id}`}
                              size={90}
                            />
                          </div>
                        </div>
                        <p className="text-sm text-black font-medium">www.iinsaf.com</p>
                        <p className="text-[11px] text-black font-bold uppercase">(RNI:HRBIL/25/A2063)</p>
                        <p className="text-[10px] text-gray-500 font-bold italic">Digitally Verified Identity</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <button
              onClick={() => navigate('/admin/approvedcards')}
              className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-md shadow transition duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
            >
              Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DownloadReporterIdCards;
